/**
 * @name: useEmailDataStore
 * @description: 邮件数据全局状态管理（标签、文件夹等）
 * @features: 
 * - 全局缓存标签和文件夹数据
 * - 按需刷新，避免重复请求
 * - 支持强制刷新
 */
import {defineStore} from 'pinia';
import {getLabelListData, getFolderList} from '../api/emailApi';

interface EmailLabel {
  id: string | number;
  labelName: string;
  labelColor?: string;
  [key: string]: any;
}

interface EmailFolder {
  id: string | number;
  folderName: string;
  [key: string]: any;
}

interface EmailDataState {
  // 标签数据
  labels: EmailLabel[];
  labelsLoaded: boolean;
  labelsLoading: boolean;
  labelsLastFetchTime: number;
  
  // 文件夹数据
  folders: EmailFolder[];
  foldersLoaded: boolean;
  foldersLoading: boolean;
  foldersLastFetchTime: number;
  
  // 缓存过期时间（毫秒），默认5分钟
  cacheExpireTime: number;
  
  // 正在进行的请求承诺，用于防止并发重复请求
  labelsPromise: Promise<EmailLabel[]> | null;
  foldersPromise: Promise<EmailFolder[]> | null;
}

export const useEmailDataStore = defineStore('emailData', {
  state: (): EmailDataState => ({
    labels: [],
    labelsLoaded: false,
    labelsLoading: false,
    labelsLastFetchTime: 0,
    
    folders: [],
    foldersLoaded: false,
    foldersLoading: false,
    foldersLastFetchTime: 0,
    
    cacheExpireTime: 5 * 60 * 1000, // 5分钟
    
    labelsPromise: null,
    foldersPromise: null,
  }),
  
  getters: {
    // 标签是否需要刷新
    shouldRefreshLabels(state): boolean {
      if (!state.labelsLoaded) return true;
      const now = Date.now();
      return now - state.labelsLastFetchTime > state.cacheExpireTime;
    },
    
    // 文件夹是否需要刷新
    shouldRefreshFolders(state): boolean {
      if (!state.foldersLoaded) return true;
      const now = Date.now();
      return now - state.foldersLastFetchTime > state.cacheExpireTime;
    },
  },
  
  actions: {
    /**
     * 获取标签列表（带缓存）
     * @param forceRefresh 是否强制刷新
     */
    async fetchLabels(forceRefresh = false) {
      // 如果正在加载，返回已有的 Promise，实现并发合并
      if (this.labelsPromise) {
        return this.labelsPromise;
      }
      
      // 如果有缓存且未过期且非强制刷新，直接返回缓存
      if (!forceRefresh && this.labelsLoaded && !this.shouldRefreshLabels) {
        console.log('[EmailDataStore] 使用标签缓存数据');
        return this.labels;
      }
      
      this.labelsLoading = true;
      // 创建一个新的 Promise 并赋值给 labelsPromise
      this.labelsPromise = (async () => {
        try {
          console.log('[EmailDataStore] 从服务器获取标签数据');
          const res = await getLabelListData();
          if (res && res.records) {
            this.labels = res.records || [];
            this.labelsLoaded = true;
            this.labelsLastFetchTime = Date.now();
          }
          return this.labels;
        } catch (error: any) {
          console.error('[EmailDataStore] 加载标签失败:', error);
          throw error;
        } finally {
          this.labelsLoading = false;
          this.labelsPromise = null; // 请求完成后重置 Promise
        }
      })();
      
      return this.labelsPromise;
    },
    
    /**
     * 获取文件夹列表（带缓存）
     * @param forceRefresh 是否强制刷新
     */
    async fetchFolders(forceRefresh = false) {
      // 如果正在加载，返回已有的 Promise，实现并发合并
      if (this.foldersPromise) {
        return this.foldersPromise;
      }
      
      // 如果有缓存且未过期且非强制刷新，直接返回缓存
      if (!forceRefresh && this.foldersLoaded && !this.shouldRefreshFolders) {
        console.log('[EmailDataStore] 使用文件夹缓存数据');
        return this.folders;
      }
      
      this.foldersLoading = true;
      // 创建一个新的 Promise 并赋值给 foldersPromise
      this.foldersPromise = (async () => {
        try {
          console.log('[EmailDataStore] 从服务器获取文件夹数据');
          const res = await getFolderList({folderType: '5'});
          if (res && res.records) {
            this.folders = res.records || [];
            this.foldersLoaded = true;
            this.foldersLastFetchTime = Date.now();
          }
          return this.folders;
        } catch (error: any) {
          console.error('[EmailDataStore] 加载文件夹失败:', error);
          throw error;
        } finally {
          this.foldersLoading = false;
          this.foldersPromise = null; // 请求完成后重置 Promise
        }
      })();
      
      return this.foldersPromise;
    },
    
    /**
     * 添加标签后刷新
     */
    async refreshLabelsAfterAdd() {
      await this.fetchLabels(true);
    },
    
    /**
     * 添加文件夹后刷新
     */
    async refreshFoldersAfterAdd() {
      await this.fetchFolders(true);
    },
    
    /**
     * 删除标签后刷新
     */
    async refreshLabelsAfterDelete() {
      await this.fetchLabels(true);
    },
    
    /**
     * 删除文件夹后刷新
     */
    async refreshFoldersAfterDelete() {
      await this.fetchFolders(true);
    },
    
    /**
     * 清空所有缓存
     */
    clearCache() {
      this.labels = [];
      this.labelsLoaded = false;
      this.labelsLastFetchTime = 0;
      
      this.folders = [];
      this.foldersLoaded = false;
      this.foldersLastFetchTime = 0;
      
      console.log('[EmailDataStore] 缓存已清空');
    },
    
    /**
     * 设置缓存过期时间
     * @param milliseconds 毫秒数
     */
    setCacheExpireTime(milliseconds: number) {
      this.cacheExpireTime = milliseconds;
    },
  },
  
  // 持久化配置（可选，根据需求决定是否需要持久化）
  // persist: {
  //   enabled: true,
  //   strategies: [
  //     {
  //       key: 'email-data',
  //       storage: sessionStorage, // 使用 sessionStorage，关闭浏览器就清空
  //     },
  //   ],
  // },
});
